
import { Options, Vue } from "vue-class-component";
import _ from "lodash";
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import Header from "@/components/Header.vue";
import Module from "@/components/Module.vue";
import Elements from "@/components/Elements.vue";
import ApiService from "../services/ApiService";
import { wrapper } from "../utils/wrapper";
import { useStore } from "vuex";
import StorageService from "@/services/StorageService";
import { router } from "@/router";
import { getModuleTitle } from "../utils/modules";
import { getPageTitle, scrollToTop } from "../utils/pages";
import { closePage, createAction, openPage } from "@/utils/patientLoggerPage";
import { calculateTreamentProgress } from "@/utils/progress";

@Options({
  components: {
    Breadcrumbs,
    Header,
    Module,
    Elements
  },
  methods: {
    getModuleTitle: getModuleTitle,
    getPageTitle: getPageTitle
  }
} as any)
export default class ModulePage extends Vue {
  viewName = "ModulePage";
  patientLoggerPageId: any;
  private storageService = new StorageService();
  private apiService = new ApiService();
  globalStore = useStore();
  user: any = null;
  language: any = null;
  treatmentSet = false;
  treatmentId: any = null;
  moduleId: any = null; // from the URL params
  pageId: any = null;
  pageNumber: any = null; // from the URL params
  pageStateId: any = null;
  firstPage: any = null;
  lastPage: any = null;
  module: any = {};
  moduleState: any = {};
  page: any = {};
  firstElement: any = {};
  elements: any[] = [];
  groups: any[] = [];
  currentModule: any = {};
  events: any[] = [];
  readOnly = false;
  attempts: any = null;
  isLoading = false;

  async created() {
    this.user = this.storageService.getUser();
    this.language = this.user.language;
    await this.setupPage();
  }

  async beforeUnmount() {
    if (this.patientLoggerPageId && this.storageService.getUser()) {
      await closePage(this.patientLoggerPageId, this.user.patientsId);
    }
  }

  async setupPage() {
    this.isLoading = true;
    this.moduleId = Number(this.$route.params.moduleId);
    this.pageNumber = Number(this.$route.params.pageId);
    this.pageStateId = Number(this.$route.params.pageStateId);

    await this.getTreatment();
    await this.getModules();
    await this.getBreadcrumbs();
    await this.getModule();
    await this.getPage();

    // This covers the case where the page is already opened so we need to close it first
    if (this.patientLoggerPageId) {
      await closePage(this.patientLoggerPageId, this.user.patientsId);
      this.patientLoggerPageId = undefined;
    }
    // Opens the page on first load or a page re-rendering (from a next/previous page action)
    this.patientLoggerPageId = await openPage(
      this.viewName,
      this.user.patientsId
    );

    await createAction(
      "module_entry",
      this.patientLoggerPageId,
      this.moduleId,
      this.pageId,
      this.user.patientsId
    );
    this.isLoading = false;
  }

  async getTreatment() {
    const dataResponse = await wrapper(
      this.apiService.getTreatment(this.user.patientsId)
    );
    if (dataResponse.error) {
      console.error("get modules error ", dataResponse.error);
      this.globalStore.commit("open", {
        type: "danger",
        title: this.$t("ALERTS.MODULES.GET_TREATMENT.FAILED.TITLE"),
        text: this.$t("ALERTS.MODULES.GET_TREATMENT.FAILED.TEXT")
      });
    }

    if (dataResponse.data) {
      if (Object.entries(dataResponse.data).length === 0) {
        this.treatmentSet = false;
      } else {
        this.treatmentSet = true;
        this.treatmentId = dataResponse.data.id;
      }
    }
  }

  async getModules() {
    if (this.treatmentId) {
      const dataResponse = await wrapper(
        this.apiService.getModules(this.user.patientsId, this.treatmentId)
      );
      if (dataResponse.error) {
        console.error("get current module error ", dataResponse.error.data);
        this.globalStore.commit("open", {
          type: "danger",
          title: this.$t("ALERTS.MODULES.GET.FAILED.TITLE"),
          text: this.$t("ALERTS.MODULES.GET.FAILED.TEXT")
        });
      }

      if (dataResponse.data) {
        this.treatmentSet = true;
        for (const mod of dataResponse.data) {
          if (mod.id == this.moduleId) {
            for (const modState of mod.modulesStates) {
              this.moduleState = modState;
            }
          }
        }
        this.attempts = this.moduleState.attempts;
      }
    }
  }

  async getBreadcrumbs() {
    const dataResponse = await wrapper(
      this.apiService.getPageGroups(this.user.patientsId, this.pageNumber)
    );
    if (dataResponse.error) {
      console.error("get groups error ", dataResponse.error.data);
      if (
        dataResponse.error.data &&
        dataResponse.error.data.meta &&
        (dataResponse.error.data.meta.error_code == "ERROR_PAGE_DO_NOT_EXIST" ||
          dataResponse.error.data.meta.error_code ==
          "ERROR_GETTING_MODULE_STATE")
      ) {
        router.push({ name: "NotFound" });
      } else {
        this.globalStore.commit("open", {
          type: "danger",
          title: this.$t("ALERTS.MODULE_PAGE.GET_GROUPS.FAILED.TITLE"),
          text: this.$t("ALERTS.MODULE_PAGE.GET_GROUPS.FAILED.TEXT")
        });
      }
    }

    if (dataResponse.data) {
      this.treatmentSet = true;
      this.groups = dataResponse.data;
    }
  }

  async getModule() {
    const dataResponse = await wrapper(
      this.apiService.getModule(this.user.patientsId, this.moduleId)
    );
    if (dataResponse.error) {
      console.error("get module error ", dataResponse.error);
      this.globalStore.commit("open", {
        type: "danger",
        title: this.$t("ALERTS.MODULE_PAGE.GET_MODULE.FAILED.TITLE"),
        text: this.$t("ALERTS.MODULE_PAGE.GET_MODULE.FAILED.TEXT")
      });
    }

    if (
      dataResponse.data &&
      dataResponse.data.pages &&
      dataResponse.data.pages[0]
    ) {
      this.module = dataResponse.data;
      const totalPages = dataResponse.data.pages.length;
      for (const page of dataResponse.data.pages) {
        if (page.order == 1) {
          this.firstPage = page;
        }
        if (page.order == totalPages) {
          this.lastPage = page;
        }
      }
    }
  }

  async getPage() {
    const dataResponse = await wrapper(
      this.apiService.getPage(this.user.patientsId, this.pageNumber)
    );
    if (dataResponse.error) {
      console.error("get page error ", dataResponse.error);
      this.globalStore.commit("open", {
        type: "danger",
        title: this.$t("ALERTS.MODULE_PAGE.GET_PAGE.FAILED.TITLE"),
        text: this.$t("ALERTS.MODULE_PAGE.GET_PAGE.FAILED.TEXT")
      });
    }

    if (dataResponse.data) {
      this.page = dataResponse.data;
      this.pageId = this.page.id;
      this.elements = await this.formatPageElements(this.page.pagesElements);
      this.firstElement = this.page.pagesElements[0];
      if (this.attempts === 0) {
        this.readOnly = true;
      }
    }
  }

  translatePageElements(elements: any) {
    return elements.map((element: any) => {
      if (element.translations) {
        if (element.translations[this.language]) {
          const translations = element.translations[this.language];
          element.options = { ...element.options, ...translations };
        }
      }
      return element;
    });
  }

  async formatPageElements(elements: any): Promise<any[]> {
    elements = this.translatePageElements(elements);

    const templateElements = [];
    for (const element of elements) {
      if (element.templatesId) {
        //format element's templates elements to match old API
        if (element.options.type == "TEMPLATE") {
          for (const templateElement of element.templates.templatesElements) {
            templateElements.push({
              ...templateElement,
              pagesElementsId: element.id
            });
          }
        }
      }

      //format element's references to match old API
      if (element.refs.length > 0 && element.refs[0].pageElement.answers) {
        element.ref = {
          ...element.refs[0].pageElement,
          Answer: [...element.refs[0].pageElement.answers]
        };

        if (element.refs[0].pageElement.options.type !== "LIST-INPUT") {
          if (element.refs[0].pageElement.answers[0].value) {
            element.ref.Answer[0].value = [
              {
                value: JSON.parse(element.refs[0].pageElement.answers[0]?.value)
              }
            ];
          }
        } else {
          if (element.refs[0].pageElement.answers[0].value) {
            element.ref.Answer[0].value = [
              ...JSON.parse(element.refs[0].pageElement.answers[0]?.value)
            ];
          }
        }
      }
    }
    if (templateElements.length > 0) {
      elements.push(...templateElements);
    }

    //format element's answers to match old API
    const arrayOfElements = elements.map((e: any) => {
      e.Answer = [];
      e.originalAnswer = [];

      if (e.answers) {
        if (e.answers.value) {
          if (e.answers.value != " ") {
            const answer = JSON.parse(e.answers.value);
            if (e.options.type === "LIST-INPUT") {
              for (const ans of answer) {
                e.Answer.push(ans);
              }
            } else {
              e.Answer.push({
                value: answer
              });
            }
          }
        }
      }
      e.originalAnswer = _.cloneDeep(e.Answer);
      return e;
    });

    return arrayOfElements;
  }

  async moveToPreviousPage() {
    let previousPageStateId;
    await this.savePage();
    if (this.page.id !== this.firstPage.id) {
      const previousPage = this.module.pages.find(
        (p: any) => p.order == this.page.order - 1
      );

      previousPageStateId = _.sortBy(
        previousPage.pagesStates,
        (ps: any) => ps.createdAt
      )?.pop()?.id;

      await this.moveToPage(previousPage.id, previousPageStateId);
      scrollToTop()
    }
  }

  async moveToNextPage() {
    let nextPageStateId;
    const savedPage = await this.savePage();
    if (savedPage) {
      this.globalStore.commit('setMainProgress', await calculateTreamentProgress(this.user.patientsId, this.treatmentId));

      if (this.page.id !== this.lastPage.id) {
        const nextPage = this.module.pages.find(
          (p: any) => p.order == this.page.order + 1
        );

        nextPageStateId = _.sortBy(
          nextPage.pagesStates,
          (ps: any) => ps.createdAt
        )?.pop()?.id;

        await this.moveToPage(nextPage.id, nextPageStateId);
        scrollToTop()
      }
    } else {
      this.globalStore.commit("open", {
        type: "danger",
        text: this.$t("ALERTS.MODULE_PAGE.SAVE_PAGE_REQUIRED.FAILED.TEXT")
      });
    }
  }

  async moveToPage(pageId: string, pageStateId: string) {
    await router.push({
      name: "Page",
      params: {
        moduleId: this.moduleId,
        pageId,
        pageStateId
      }
    });

    await this.setupPage();
    scrollToTop()
  }

  async endModule() {
    if (this.lastPage && this.attempts > 0) {
      const savedPage = await this.savePage();
      if (savedPage) {
        const endModuleRequest = await wrapper(
          this.apiService.endModule(
            this.user.patientsId,
            this.moduleId,
            this.moduleState.progress
          )
        );

        if (endModuleRequest.error) {
          console.error("get save page error ", endModuleRequest.error);
          this.globalStore.commit("open", {
            type: "danger",
            title: this.$t("ALERTS.MODULE_PAGE.END_MODULE.FAILED.TITLE"),
            text: this.$t("ALERTS.MODULE_PAGE.END_MODULE.FAILED.TEXT")
          });
        }

        if (endModuleRequest.data) {
          const treatmentState = await wrapper(this.apiService.getTreatmentState(this.user.patientsId, this.treatmentId))
          if (treatmentState.error) {
            console.error("get treatment state error ", endModuleRequest.error);
            this.globalStore.commit("open", {
              type: "danger",
              title: this.$t("ALERTS.MODULE_PAGE.END_MODULE.FAILED.TITLE"),
              text: this.$t("ALERTS.MODULE_PAGE.END_MODULE.FAILED.TEXT")
            });
          } else {
            this.globalStore.commit('setMainProgress', treatmentState.data.data[0].progress)
          }

          await createAction(
            "module_end",
            this.patientLoggerPageId,
            this.moduleId,
            this.pageId,
            this.user.patientsId
          );
          const inAppBrowser = await this.storageService.get('inAppBrowserToken')
          if (inAppBrowser) router.push({ name: "CloseInAppBrowser" });
          else router.push({ name: "Modules" });

        }
      } else {
        this.globalStore.commit("open", {
          type: "danger",
          text: this.$t("ALERTS.MODULE_PAGE.SAVE_PAGE_REQUIRED.FAILED.TEXT")
        });
      }
    }
  }

  async verifyRequired(elements: string | any[]) {
    for (let i = 0; i < elements.length; i++) {
      if (elements[i].required && elements[i].visible) {
        if (
          elements[i].options.type == "LARGE-TEXT-INPUT" ||
          elements[i].options.type == "TEXT-INPUT" ||
          elements[i].options.type == "SELECT-INPUT" ||
          elements[i].options.type == "DATE-PICKER"
        ) {
          if (
            elements[i].Answer.length == 0 ||
            (!elements[i].Answer[0]?.value &&
              typeof elements[i].Answer[0]?.value !== "number")
          ) {
            elements[i].danger = true; //marks the element as required
            return false;
          }
        } else if (
          elements[i].options.type == "LIST-INPUT" ||
          elements[i].options.type == "CALENDAR"
        ) {
          if (elements[i].Answer.length === 0) {
            elements[i].danger = true;
            return false;
          }
        } else if (elements[i].options.type == "CHECKBOX") {
          if (
            elements[i].Answer.length === 0 ||
            elements[i].Answer[0].value.length === 0
          ) {
            elements[i].danger = true;
            return false;
          }
          if (elements[i].Answer[0].value) {
            for (const answer of elements[i].Answer[0].value) {
              if (answer.uuid) {
                if (answer.value.length == 0) {
                  elements[i].danger = true;
                  return false;
                }
              } else {
                if (answer.length == 0) {
                  elements[i].danger = true;
                  return false;
                }
              }
            }
          }
        } else if (elements[i].options.type == "SLIDER") {
          if (elements[i].Answer.length === 0) {
            elements[i].danger = true;
            return false;
          } else {
            const { value } = elements[i].Answer[0];
            if (value === null || value === undefined || value === "") {
              elements[i].danger = true;
              return false;
            }
          }
        }
      }
      // Here the element is valid and we can remove the required mark.
      if (elements[i].options.type !== "SUMMARY") {
        elements[i].danger = false;
      }
    }
    return true;
  }

  async getModifiedElements(elements: any[]) {
    const modifiedElements: any[] = [];

    for (const element of elements) {
      if (!_.isEqual(element.Answer, element.originalAnswer)) {
        modifiedElements.push(element);
      }
    }

    return modifiedElements;
  }

  async splitPageAndTemplateElements(elements: any[]) {
    const pageElements: any[] = [];
    const templateElements: any[] = [];
    let pageElementId: any;

    elements.forEach(element => {
      if (element.templatesId) {
        if (element.options.type === "TEMPLATE") {
          pageElementId = element.id;
        }
        const e = {
          ...element,
          pageElementId: pageElementId
        };
        templateElements.push(e);
      } else {
        pageElements.push(element);
      }
    });

    return {
      pageElements,
      templateElements
    };
  }

  async getEvents() {
    const dataResponse = await wrapper(
      this.apiService.getEvents(this.user.patientsId)
    );
    if (dataResponse.error) {
      console.error("get events error ", dataResponse.error);
      this.globalStore.commit("open", {
        type: "danger",
        title: this.$t("ALERTS.EVENTS.GET.FAILED.TITLE"),
        text: this.$t("ALERTS.EVENTS.GET.FAILED.TEXT")
      });
    }

    if (dataResponse.data) {
      this.events = dataResponse.data;
    }
  }

  async savePage() {
    if (!(await this.verifyRequired(this.elements))) {
      return false;
    }

    const {
      pageElements,
      templateElements
    } = await this.splitPageAndTemplateElements(this.elements);
    const modifiedElements = await this.getModifiedElements(pageElements);
    const modifiedTemplateElements = await this.getModifiedElements(
      templateElements
    );

    if (!modifiedElements.length && !modifiedTemplateElements.length) {
      await this.apiService.changePage(this.user.patientsId, this.pageStateId);
      // nothing changed. nothing else to do.
      return true;
    }

    if (modifiedElements.length || modifiedTemplateElements.length) {
      const formatTemplateElementArrayForApi = [];
      const formatElementArrayForApi = [];
      let formatForApiElement = {};

      if (modifiedTemplateElements.length) {
        for (const element of modifiedTemplateElements) {
          if (element.options.type == "CALENDAR") {
            await this.getEvents();
            if (this.events) {
              for (const e of this.events) {
                if (e.pagesElementsId == element.id) {
                  formatForApiElement = {
                    value: JSON.stringify(element.Answer),
                    templatesElementsId: element?.id,
                    pagesElementsId: element?.pagesElementsId,
                    eventsId: e.id
                  };
                }
                if (Object.entries(formatForApiElement).length !== 0) {
                  formatTemplateElementArrayForApi.push(formatForApiElement);
                }
              }
            }
          } else {
            formatForApiElement = {
              value:
                element.options.type == "LIST-INPUT"
                  ? JSON.stringify(element.Answer)
                  : JSON.stringify(element.Answer[0].value),
              templatesElementsId: element?.id,
              pagesElementsId: element?.pagesElementsId
            };

            if (Object.entries(formatForApiElement).length !== 0) {
              formatTemplateElementArrayForApi.push(formatForApiElement);
            }
          }
        }
      }

      if (modifiedElements.length) {

        for (const element of modifiedElements) {
          if (element.options.type == "CALENDAR") {
            await this.getEvents();
            if (this.events) {
              for (const e of this.events) {
                if (e.pagesElementsId != null && e.pagesElementsId == element.id) {
                  for (const answer of element.Answer) {
                    if (answer.id == e.id) {
                      formatForApiElement = {
                        value: JSON.stringify(answer),
                        pagesElementsId: element?.id,
                        eventsId: e.id
                      };
                    }
                  }
                }
                if (Object.entries(formatForApiElement).length !== 0) {
                  formatElementArrayForApi.push(formatForApiElement);
                }
              }
            }
          } else {
            //if it's a page element and there are references
            if (
              element.refs &&
              element.refs[0] &&
              element.refs[0]?.sourcePagesElementsId == element?.id
            ) {
              formatForApiElement = {
                value:
                  element.options.type == "LIST-INPUT"
                    ? JSON.stringify(element.Answer)
                    : JSON.stringify(element.Answer[0].value),
                pagesElementsId: element?.id,
                refsId: element.refs[0].id
              };
            } else {
              // if there are no references
              formatForApiElement = {
                value:
                  element.options.type == "LIST-INPUT"
                    ? JSON.stringify(element.Answer)
                    : JSON.stringify(element.Answer[0].value),
                pagesElementsId: element?.id
              };
            }
            if (Object.entries(formatForApiElement).length !== 0) {
              formatElementArrayForApi.push(formatForApiElement);
            }
          }
        }
      }

      const allElementsModified = [
        ...formatElementArrayForApi,
        ...formatTemplateElementArrayForApi
      ];

      const dataResponse = await wrapper(
        this.apiService.saveAnswer(
          this.user.patientsId,
          this.pageId,
          allElementsModified
        )
      );

      if (dataResponse.error) {
        console.error("get save element error ", dataResponse.error);
        this.globalStore.commit("open", {
          type: "danger",
          title: this.$t("ALERTS.MODULE_PAGE.SAVE_PAGE.FAILED.TITLE"),
          text: this.$t("ALERTS.MODULE_PAGE.SAVE_PAGE.FAILED.TEXT")
        });
      } else {
        const dataResponse = await wrapper(
          this.apiService.changePage(this.user.patientsId, this.pageStateId)
        );

        if (dataResponse.error) {
          console.error("change page status", dataResponse.error);
          this.globalStore.commit("open", {
            type: "danger",
            title: this.$t("ALERTS.MODULE_PAGE.GET_PAGE.FAILED.TITLE"),
            text: this.$t("ALERTS.MODULE_PAGE.GET_PAGE.FAILED.TEXT")
          });
        }

        await createAction(
          "module_save",
          this.patientLoggerPageId,
          this.moduleId,
          this.pageId,
          this.user.patientsId
        );
        return true;
      }
    }
  }
}
